import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { DynamicLinkService } from './dynamic-link.service';
import { LocalStorageService } from './localstorage.service';

export interface HtmlCtaClickOptions {
  onEmptyLink?: () => void;
}

export interface HtmlCtaTransformOptions {
  preserveDocument?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class HtmlCtaService {
  constructor(
    private appService: AppService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private dynamicLinkService: DynamicLinkService,
  ) {}

  handleClick(event: Event, options: HtmlCtaClickOptions = {}) {
    const ctaButton = this.getCtaButton(event);

    if (!ctaButton) return false;

    event.preventDefault();
    const link = this.getCtaLink(ctaButton);
    this.navigate(link, options);
    return true;
  }

  transformCtaButtons(html: string, options: HtmlCtaTransformOptions = {}) {
    if (!html) return '';

    const document = new DOMParser().parseFromString(html, 'text/html');

    document.querySelectorAll<HTMLElement>('.cta-button').forEach(ctaButton => {
      const text = this.getCtaText(ctaButton);
      if (text) ctaButton.textContent = text;
    });

    if (options.preserveDocument && /<(?:html|head|body)\b/i.test(html)) {
      return document.documentElement.outerHTML;
    }

    return document.body.innerHTML;
  }

  navigate(rawLink: string, options: HtmlCtaClickOptions = {}) {
    const link = (rawLink || '').trim();
    if (!this.dynamicLinkService.hasDomainPlaceholder(link)) { this.navigateLegacy(link, options); return; }
    const resolvedLink = this.dynamicLinkService.replaceDomainPlaceholder(link);
    if (!resolvedLink) { options.onEmptyLink?.(); return; }
    if (this.isBlockedLink(resolvedLink)) return;
    const internalPath = this.dynamicLinkService.getInternalPath(resolvedLink);
    if (this.postNativeAppCta(internalPath || resolvedLink)) return;
    if (internalPath) { this.navigateInternal(internalPath); return; }
    if (/^https?:\/\//i.test(resolvedLink)) { window.open(resolvedLink, '_blank', 'noopener'); return; }
    this.navigateInternal(resolvedLink);
  }

  private navigateLegacy(link: string, options: HtmlCtaClickOptions) {

    if (!link) {
      options.onEmptyLink?.();
      return;
    }

    if (this.isBlockedLink(link)) return;

    if (this.postNativeAppCta(link)) return;

    if (/^https?:\/\//i.test(link)) {
      window.open(link, '_blank', 'noopener');
      return;
    }

    this.navigateInternal(link);
  }

  private navigateInternal(link: string) {
    const splitIndex = link.search(/[?#]/);
    const pathPart = splitIndex === -1 ? link : link.slice(0, splitIndex);
    const suffix = splitIndex === -1 ? '' : link.slice(splitIndex);
    const normalizedPath = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
    const languageCode = this.appService.languageCode;
    const firstSegment = normalizedPath.split('/')[1] || '';
    const finalPath =
      languageCode && firstSegment !== languageCode ? `/${languageCode}${normalizedPath}` : normalizedPath;

    this.router.navigateByUrl(`${finalPath}${suffix}`);
  }

  /**
   * App WebView 內點擊 CTA 時，不讓網頁自行跳轉，改由原生 App 接手開啟對應頁面。
   * path 會保留後台設定的 CTA URL，例如 /deposit/fiat?currency=CNY&amount=0。
   *
   * @param path CTA 後台設定的原始連結
   */
  private postNativeAppCta(path: string) {
    if (!this.appService.isNativeApp) return false;

    const actionApp = (
      window as Window & {
        actionApp?: {
          postMessage?: (message: string) => void;
        };
      }
    ).actionApp;

    actionApp?.postMessage?.(
      JSON.stringify({
        method: 'openAppNative',
        path,
      }),
    );

    return true;
  }

  private isBlockedLink(link: string) {
    const normalizedLink = link.replace(/[\u0000-\u001F\u007F\s]+/g, '').toLowerCase();
    const hasUnsupportedProtocol = /^[a-z][a-z0-9+.-]*:/i.test(normalizedLink) && !/^https?:\/\//i.test(normalizedLink);

    return /^\/\//.test(normalizedLink) || hasUnsupportedProtocol;
  }

  private getCtaLink(ctaButton: HTMLElement) {
    if (this.isLoggedIn) {
      return ctaButton.getAttribute('data-link') || ctaButton.getAttribute('href') || '';
    }

    return (
      ctaButton.getAttribute('data-logged-out-link') ||
      ctaButton.getAttribute('data-link') ||
      ctaButton.getAttribute('href') ||
      ''
    );
  }

  private getCtaButton(event: Event) {
    const pathCtaButton = event
      .composedPath()
      .find(target => target instanceof HTMLElement && target.classList.contains('cta-button'));

    if (pathCtaButton instanceof HTMLElement) return pathCtaButton;

    const target = event.target as HTMLElement | null;
    return target?.closest?.('.cta-button') as HTMLElement | null;
  }

  private getCtaText(ctaButton: HTMLElement) {
    if (this.isLoggedIn) {
      return ctaButton.getAttribute('data-text') || '';
    }

    return ctaButton.getAttribute('data-logged-out-text') || ctaButton.getAttribute('data-text') || '';
  }

  private get isLoggedIn() {
    return Boolean(this.localStorageService.loginToken);
  }
}
